import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  AlertCircle,
  FileText,
  RefreshCw,
  Search,
} from 'lucide-react';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import PaginationControls from '@/components/shared/PaginationControls';
import TemplateCard from '@/components/templates/TemplateCard';
import {
  deleteMealPlanTemplate,
  getMealPlanTemplate,
  listMealPlanTemplates,
  type MealPlanTemplateKind,
  type MealPlanTemplateDetails,
  type MealPlanTemplateDay,
  type MealPlanTemplateSummary,
  updateMealPlanTemplate,
} from '@/lib/api/mealPlans';
import { getCurrentUser } from '@/lib/api/auth';
import { extractErrorMessage, showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { formatNutritionApiRaw } from '@/utils/nutrition-display-format';

const ACCENT = '#20AEDC';
const PAGE_SIZE = 10;
const TOTALS_PAGE_SIZE = 100;

type TemplateTab = MealPlanTemplateKind;

type PaginationMeta = Record<string, unknown> | undefined;

const getPaginationNumber = (
  pagination: PaginationMeta,
  keys: string[],
  fallback = 0,
): number => {
  if (!pagination) return fallback;

  for (const key of keys) {
    const raw = pagination[key];
    const value = typeof raw === 'string' ? Number(raw) : raw;
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, Math.floor(value));
    }
  }

  return fallback;
};

const getTotalPages = (pagination: PaginationMeta, loadedCount: number): number => {
  const explicitTotalPages = getPaginationNumber(
    pagination,
    ['totalPages', 'pages', 'pageCount', 'total_pages'],
    0,
  );
  if (explicitTotalPages > 0) return explicitTotalPages;

  const total = getPaginationNumber(
    pagination,
    ['total', 'totalCount', 'totalItems', 'count', 'total_count'],
    loadedCount,
  );
  const pageSize = getPaginationNumber(
    pagination,
    ['pageSize', 'limit', 'perPage', 'page_size'],
    PAGE_SIZE,
  );

  return Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
};

const getTemplateTabFromSearchParams = (params: URLSearchParams): TemplateTab =>
  params.get('tab') === 'meal_slot' ? 'meal_slot' : 'meal_plan';

type TemplateEditorFormState = {
  templateName: string;
  description: string;
  suitableFor: string;
  clinicalTagsCsv: string;
  visibility: 'private' | 'shared_with_practice';
};

const EMPTY_EDITOR_FORM: TemplateEditorFormState = {
  templateName: '',
  description: '',
  suitableFor: '',
  clinicalTagsCsv: '',
  visibility: 'private',
};

/** Tabs row — matches Figma (node 172:1844–1860): inactive 14px + gray pill; active 16px medium + brand pill + 2px underline. */
function TemplateListTab({
  label,
  count,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  if (selected) {
    return (
      <Box
        component="button"
        type="button"
        role="tab"
        aria-selected
        onClick={onClick}
        sx={{
          border: 'none',
          bgcolor: 'transparent',
          cursor: 'pointer',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '10px',
          height: 46,
          justifyContent: 'flex-end',
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '20px',
              color: ACCENT,
            }}
          >
            {label}
          </Typography>
          <Box
            sx={{
              bgcolor: ACCENT,
              height: 20,
              px: 1,
              py: '2px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 20,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: '16px',
                color: '#fff',
              }}
            >
              {count}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ height: '2px', width: '100%', bgcolor: ACCENT, borderRadius: '1px' }} />
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      role="tab"
      aria-selected={false}
      onClick={onClick}
      sx={{
        border: 'none',
        bgcolor: 'transparent',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        px: 2,
        py: '11px',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          color: '#717182',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          bgcolor: '#ececf0',
          height: 20,
          px: 1,
          py: '2px',
          borderRadius: 999,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 22,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px',
            color: '#717182',
          }}
        >
          {count}
        </Typography>
      </Box>
    </Box>
  );
}

const parseTagsFromCsv = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0),
    ),
  );

const toTemplateEditorFormState = (detail: MealPlanTemplateDetails): TemplateEditorFormState => ({
  templateName: detail.templateName || detail.name || '',
  description: detail.description || '',
  suitableFor: detail.suitableFor || '',
  clinicalTagsCsv: (detail.clinicalTags || []).join(', '),
  visibility: detail.visibility === 'shared_with_practice' ? 'shared_with_practice' : 'private',
});

const TemplateSkeletonGrid = () => (
  <div className="grid grid-cols-1 gap-5 pb-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3">
    {Array.from({ length: 6 }, (_, index) => (
      <div
        key={index}
        className="h-[248px] animate-pulse rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm"
      >
        <div className="mb-4 flex items-start gap-3">
          <div className="h-12 w-12 rounded-full bg-slate-100" />
          <div className="flex-1 space-y-2 pt-1">
            <div className="h-4 w-3/4 rounded bg-slate-100" />
            <div className="h-3 w-1/2 rounded bg-slate-100" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-3 rounded bg-slate-100" />
          <div className="h-3 w-5/6 rounded bg-slate-100" />
          <div className="h-3 w-2/3 rounded bg-slate-100" />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <div className="h-9 rounded-lg bg-slate-100" />
          <div className="h-9 rounded-lg bg-slate-100" />
        </div>
      </div>
    ))}
  </div>
);

const TemplatesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = React.useState<MealPlanTemplateSummary[]>([]);
  const [pagination, setPagination] = React.useState<PaginationMeta>();
  const [tabCounts, setTabCounts] = React.useState({ meal_plan: 0, meal_slot: 0 });
  const [activeTab, setActiveTab] = React.useState<TemplateTab>(() => getTemplateTabFromSearchParams(searchParams));
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editorTemplateId, setEditorTemplateId] = React.useState<string | null>(null);
  const [editorDetail, setEditorDetail] = React.useState<MealPlanTemplateDetails | null>(null);
  const [editorDays, setEditorDays] = React.useState<MealPlanTemplateDay[]>([]);
  const [editorForm, setEditorForm] = React.useState<TemplateEditorFormState>(EMPTY_EDITOR_FORM);
  const [editorLoading, setEditorLoading] = React.useState(false);
  const [editorSaving, setEditorSaving] = React.useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = React.useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [pendingDeleteTemplate, setPendingDeleteTemplate] = React.useState<MealPlanTemplateSummary | null>(null);
  const [editorError, setEditorError] = React.useState('');
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const currentUser = getCurrentUser() as Record<string, unknown> | null;
  const currentUserIdCandidates = Array.from(new Set([
    currentUser?.id,
    currentUser?.linkedProfileId,
    currentUser?.nutritionistId,
    currentUser?.clientId,
  ]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean)));

  const isOwnTemplate = React.useCallback((template: MealPlanTemplateSummary): boolean => {
    if (typeof template.isOwnTemplate === 'boolean') {
      return template.isOwnTemplate;
    }
    const createdById = typeof template.createdBy?.id === 'string' ? template.createdBy.id.trim() : '';
    return Boolean(createdById) && currentUserIdCandidates.includes(createdById);
  }, [currentUserIdCandidates]);

  const canDeleteTemplate = React.useCallback((template: MealPlanTemplateSummary): boolean => {
    if (template.canDelete === true) return true;
    if (!isOwnTemplate(template)) return false;
    if (template.canDelete === false) return false;
    return true;
  }, [isOwnTemplate]);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' && event.key !== '?') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.defaultPrevented) return;

      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable =
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        !!target?.isContentEditable;

      if (isEditable) return;
      if (!searchInputRef.current) return;

      event.preventDefault();
      searchInputRef.current.focus();
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  React.useEffect(() => {
    const nextTab = getTemplateTabFromSearchParams(searchParams);
    setActiveTab((current) => {
      if (current === nextTab) return current;
      setCurrentPage(1);
      return nextTab;
    });
  }, [searchParams]);

  React.useEffect(() => {
    let isCurrent = true;

    const fetchTabTotals = async () => {
      try {
        const [mealPlansRes, mealSlotsRes] = await Promise.all([
          listMealPlanTemplates({
            page: 1,
            pageSize: TOTALS_PAGE_SIZE,
            search: debouncedSearchQuery || undefined,
            templateKind: 'meal_plan',
          }),
          listMealPlanTemplates({
            page: 1,
            pageSize: TOTALS_PAGE_SIZE,
            search: debouncedSearchQuery || undefined,
            templateKind: 'meal_slot',
          }),
        ]);

        if (!isCurrent) return;

        const mealPlanTotal = getPaginationNumber(
          mealPlansRes.data?.pagination,
          ['total', 'totalCount', 'totalItems', 'count', 'total_count'],
          mealPlansRes.data?.templates?.length ?? 0,
        );
        const mealSlotTotal = getPaginationNumber(
          mealSlotsRes.data?.pagination,
          ['total', 'totalCount', 'totalItems', 'count', 'total_count'],
          mealSlotsRes.data?.templates?.length ?? 0,
        );

        setTabCounts({ meal_plan: mealPlanTotal, meal_slot: mealSlotTotal });
      } catch {
        if (isCurrent) setTabCounts({ meal_plan: 0, meal_slot: 0 });
      }
    };

    void fetchTabTotals();

    return () => {
      isCurrent = false;
    };
  }, [debouncedSearchQuery]);

  React.useEffect(() => {
    let isCurrent = true;

    const fetchTemplates = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await listMealPlanTemplates({
          page: currentPage,
          pageSize: PAGE_SIZE,
          search: debouncedSearchQuery || undefined,
          templateKind: activeTab,
        });

        if (!isCurrent) return;

        if (!response.success) {
          throw new Error(response.message || 'Failed to load templates');
        }

        setTemplates(response.data?.templates ?? []);
        setPagination(response.data?.pagination);
      } catch (err) {
        if (!isCurrent) return;
        setTemplates([]);
        setPagination(undefined);
        setError(extractErrorMessage(err) || 'Failed to load templates');
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    void fetchTemplates();

    return () => {
      isCurrent = false;
    };
  }, [activeTab, currentPage, debouncedSearchQuery]);

  const totalTemplatesCount = getPaginationNumber(
    pagination,
    ['total', 'totalCount', 'totalItems', 'count', 'total_count'],
    templates.length,
  );

  const totalPages = getTotalPages(pagination, templates.length);
  const hasSearch = debouncedSearchQuery.length > 0;
  const emptyTitle = hasSearch ? 'No templates found' : 'No templates yet';
  const emptyDescription = hasSearch
    ? 'Try a different search term or clear the search field.'
    : 'Saved meal plan and meal slot templates will appear here.';

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleTabChange = (value: TemplateTab) => {
    setActiveTab(value);
    setCurrentPage(1);
    const nextSearchParams = new URLSearchParams(searchParams);
    if (value === 'meal_slot') {
      nextSearchParams.set('tab', 'meal_slot');
    } else {
      nextSearchParams.delete('tab');
    }
    setSearchParams(nextSearchParams);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorTemplateId(null);
    setEditorDetail(null);
    setEditorDays([]);
    setEditorForm(EMPTY_EDITOR_FORM);
    setEditorError('');
    setEditorLoading(false);
    setEditorSaving(false);
  };

  const handleViewTemplate = (template: MealPlanTemplateSummary) => {
    const fromPath = '/templates';
    navigate(
      `/meal-plans/${encodeURIComponent(template.id)}/clients/template-preview/nutrition/1?template=true&from=${encodeURIComponent(fromPath)}`,
      {
        state: {
          canDeleteTemplate: canDeleteTemplate(template),
          ...(typeof template.canEdit === 'boolean' ? { canEditTemplate: template.canEdit } : {}),
          ...(typeof template.isOwnTemplate === 'boolean' ? { isOwnTemplate: template.isOwnTemplate } : {}),
        },
      },
    );
  };

  const handleAddTemplateToMealPlan = (_template: MealPlanTemplateSummary) => {
    navigate('/meal-planning');
  };

  const handleDeleteTemplate = (template: MealPlanTemplateSummary) => {
    setPendingDeleteTemplate(template);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirmDialog = () => {
    if (deletingTemplateId) return;
    setDeleteConfirmOpen(false);
    setPendingDeleteTemplate(null);
  };

  const handleDeleteTemplateConfirm = async () => {
    const template = pendingDeleteTemplate;
    if (!template) return;

    setDeletingTemplateId(template.id);
    try {
      const response = await deleteMealPlanTemplate(template.id);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete template');
      }

      showSuccessToast('Template deleted successfully');
      setTemplates((prev) => prev.filter((item) => item.id !== template.id));
      setTabCounts((prev) => ({
        ...prev,
        [template.templateKind]: Math.max(0, (prev[template.templateKind] || 0) - 1),
      }));
      setPagination((prev) => {
        if (!prev) return prev;
        const total = getPaginationNumber(
          prev,
          ['total', 'totalCount', 'totalItems', 'count', 'total_count'],
          templates.length,
        );
        const nextTotal = Math.max(0, total - 1);
        return {
          ...prev,
          total: nextTotal,
          totalCount: nextTotal,
          totalItems: nextTotal,
          count: nextTotal,
          total_count: nextTotal,
        };
      });

      if (templates.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => Math.max(1, prev - 1));
      }
    } catch (err) {
      showErrorToast(extractErrorMessage(err) || 'Failed to delete template');
    } finally {
      setDeletingTemplateId(null);
      setDeleteConfirmOpen(false);
      setPendingDeleteTemplate(null);
    }
  };

  const handleFormChange = (field: keyof TemplateEditorFormState, value: string) => {
    setEditorForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateDayAt = (
    dayIndex: number,
    updater: (day: MealPlanTemplateDay) => MealPlanTemplateDay,
  ) => {
    setEditorDays((prev) => prev.map((day, index) => (index === dayIndex ? updater(day) : day)));
  };

  const handleDayNameChange = (dayIndex: number, value: string) => {
    updateDayAt(dayIndex, (day) => ({
      ...day,
      dayName: value,
    }));
  };

  const handleMealFieldChange = (
    dayIndex: number,
    mealIndex: number,
    field: 'mealName' | 'mealType' | 'time',
    value: string,
  ) => {
    updateDayAt(dayIndex, (day) => ({
      ...day,
      meals: day.meals.map((meal, index) => (
        index === mealIndex
          ? {
            ...meal,
            [field]: value,
          }
          : meal
      )),
    }));
  };

  const handleItemServingsChange = (
    dayIndex: number,
    mealIndex: number,
    itemIndex: number,
    value: string,
  ) => {
    const numeric = Number(value);
    updateDayAt(dayIndex, (day) => ({
      ...day,
      meals: day.meals.map((meal, currentMealIndex) => (
        currentMealIndex === mealIndex
          ? {
            ...meal,
            items: meal.items.map((item, currentItemIndex) => (
              currentItemIndex === itemIndex
                ? {
                  ...item,
                  servings: Number.isFinite(numeric) && numeric > 0 ? numeric : item.servings,
                }
                : item
            )),
          }
          : meal
      )),
    }));
  };

  const handleSaveTemplate = async () => {
    if (!editorTemplateId) return;
    if (!editorForm.templateName.trim()) {
      setEditorError('Template name is required');
      return;
    }

    setEditorSaving(true);
    setEditorError('');

    try {
      const payloadDays = editorDays.map((day, dayIndex) => ({
        dayNumber: day.dayNumber || dayIndex + 1,
        date: day.date || null,
        dayName: day.dayName || null,
        notes: day.notes || null,
        meals: day.meals.map((meal, mealIndex) => ({
          mealId: meal.mealId,
          mealName: meal.mealName,
          mealType: meal.mealType,
          mealOrder: meal.mealOrder || mealIndex + 1,
          time: meal.time || null,
          notes: meal.notes || null,
          items: meal.items.map((item) => ({
            itemId: item.itemId || item.id,
            itemType: item.itemType,
            recipeId: item.recipeId || null,
            ingredientId: item.ingredientId || null,
            name: item.name,
            servings: item.servings,
            servingId: item.servingId || null,
            servingGrams: item.servingGrams,
            nutritionServings: item.nutritionServings,
            portionSizeMultiplier: item.portionSizeMultiplier,
            servingSizeMultiplier: item.servingSizeMultiplier,
            nutrition: item.nutrition,
            notes: item.notes || null,
          })),
        })),
      }));

      const response = await updateMealPlanTemplate(editorTemplateId, {
        templateKind: editorDetail?.templateKind,
        templateName: editorForm.templateName.trim(),
        description: editorForm.description.trim() || null,
        suitableFor: editorForm.suitableFor.trim() || null,
        clinicalTags: parseTagsFromCsv(editorForm.clinicalTagsCsv),
        visibility: editorForm.visibility,
        days: payloadDays,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update template');
      }

      setEditorDetail(response.data);
      setEditorDays(response.data.days || []);
      setEditorForm(toTemplateEditorFormState(response.data));

      setTemplates((prev) => prev.map((template) => (
        template.id === response.data!.id
          ? {
            ...template,
            name: response.data!.templateName,
            templateName: response.data!.templateName,
            description: response.data!.description || undefined,
            durationDays: response.data!.durationDays,
            startDate: response.data!.startDate || undefined,
            endDate: response.data!.endDate || undefined,
            suitableFor: response.data!.suitableFor || undefined,
            clinicalTags: response.data!.clinicalTags || [],
            visibility: response.data!.visibility,
            tags: response.data!.tags || [],
            calories: response.data!.totals?.calories,
            protein: response.data!.totals?.protein,
            carbs: response.data!.totals?.carbs,
            fat: response.data!.totals?.fat,
            fiber: response.data!.totals?.fiber,
            updatedAt: response.data!.updatedAt,
          }
          : template
      )));
    } catch (err) {
      setEditorError(extractErrorMessage(err) || 'Failed to update template');
    } finally {
      setEditorSaving(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '30px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className="px-4 pt-6 sm:px-5 lg:px-6">
          <Paper
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: '16px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fafcfe',
              px: { xs: 2, sm: 3 },
              py: 1.5,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#0f172a',
                }}
              >
                Templates
              </Typography>
            </Breadcrumbs>
          </Paper>

          {/** Figma 172:1839–1843 — title + subtitle */}
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '32px',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: '#1D2B2D',
                  textTransform: 'capitalize',
                }}
                component="h1"
              >
                Templates
              </Typography>
              <Typography
                sx={{
                  mt: '2px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: '#717182',
                }}
              >
                Manage your templates
              </Typography>
            </Box>
            <NotificationCenter buttonVariant="feature" />
          </Box>

          {/** Figma 172:1844–1863 — tabs + search row */}
          <Box
            sx={{
              mb: 3,
              borderBottom: '0.667px solid rgba(0, 0, 0, 0.1)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 2, md: 0 },
                pb: 0,
              }}
            >
              <Box
                role="tablist"
                aria-label="Template tabs"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-end',
                  gap: '20px',
                }}
              >
                <TemplateListTab
                  label="Meal Plans"
                  count={tabCounts.meal_plan}
                  selected={activeTab === 'meal_plan'}
                  onClick={() => handleTabChange('meal_plan')}
                />
                <TemplateListTab
                  label="Meal Slots"
                  count={tabCounts.meal_slot}
                  selected={activeTab === 'meal_slot'}
                  onClick={() => handleTabChange('meal_slot')}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '27px',
                  flexShrink: 0,
                  mb: { xs: 1, md: 0 },
                }}
              >
                <Box
                  component="label"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    width: 228,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    px: 1,
                    py: 0.5,
                    borderRadius: '16px',
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    '&:focus-within': { boxShadow: `0 0 0 2px ${ACCENT}40` },
                  }}
                >
                  <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Search className="h-4 w-4 shrink-0 text-[rgba(0,0,0,0.45)]" aria-hidden />
                    <input
                      ref={searchInputRef}
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onKeyDown={(event) => {
                        if (event.key !== 'Escape' || searchQuery.length === 0) return;
                        event.preventDefault();
                        event.stopPropagation();
                        setSearchQuery('');
                        setCurrentPage(1);
                      }}
                      placeholder="Search"
                      aria-label="Search templates"
                      className="min-w-0 flex-1 bg-transparent font-[Inter,sans-serif] text-[14px] leading-5 text-[#1D2B2D] outline-none placeholder:text-[rgba(0,0,0,0.2)]"
                    />
                  </Box>
                  <Box
                    sx={{
                      border: '0.5px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: '4px',
                      width: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '16px',
                        color: 'rgba(0, 0, 0, 0.2)',
                      }}
                    >
                      /
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {loading ? <TemplateSkeletonGrid /> : null}

          {!loading && error ? (
            <div className="mb-6 rounded-xl border border-red-100 bg-red-50 px-5 py-8 text-center">
              <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-400" />
              <h2 className="mb-1 text-lg font-semibold text-red-900">Could not load templates</h2>
              <p className="mx-auto mb-5 max-w-xl text-sm text-red-700">{error}</p>
              <button
                type="button"
                onClick={() => {
                  setCurrentPage(1);
                  setDebouncedSearchQuery(searchQuery.trim());
                }}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#20AEDC] px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
            </div>
          ) : null}

          {!loading && !error && templates.length === 0 ? (
            <div className="mb-6 rounded-xl border border-[#E5E7EB] bg-white px-5 py-12 text-center">
              <FileText className="mx-auto mb-4 h-14 w-14 text-slate-300" />
              <h2 className="mb-2 text-xl font-semibold text-[#1D2B2D]">{emptyTitle}</h2>
              <p className="mx-auto max-w-xl text-sm text-[#717182]">{emptyDescription}</p>
            </div>
          ) : null}

          {!loading && !error && templates.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 pb-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={{
                    ...template,
                    isOwnTemplate: isOwnTemplate(template),
                  }}
                  onViewTemplate={handleViewTemplate}
                  onDeleteTemplate={canDeleteTemplate(template) ? handleDeleteTemplate : undefined}
                  deleteDisabled={deletingTemplateId === template.id}
                />
              ))}
            </div>
          ) : null}

          {!loading && !error && templates.length > 0 ? (
            <div className="pb-8">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalCount={totalTemplatesCount}
                pageSize={PAGE_SIZE}
                showSummary
                className="justify-center"
              />
            </div>
          ) : null}
        </div>
      </Paper>

      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirmDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete template?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.95rem', color: '#475467' }}>
            {`This will permanently delete "${pendingDeleteTemplate?.templateName?.trim() || pendingDeleteTemplate?.name?.trim() || 'this template'}". This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteConfirmDialog} disabled={Boolean(deletingTemplateId)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleDeleteTemplateConfirm();
            }}
            disabled={Boolean(deletingTemplateId)}
            variant="contained"
            sx={{
              bgcolor: '#FF6262',
              '&:hover': { bgcolor: '#FF6262' },
            }}
          >
            {deletingTemplateId ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editorOpen}
        onClose={editorSaving ? undefined : closeEditor}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pb: 1 }}>
          Edit Template
        </DialogTitle>

        <DialogContent dividers sx={{ minHeight: 320 }}>
          {editorLoading ? (
            <div className="flex h-64 items-center justify-center">
              <CircularProgress size={30} />
            </div>
          ) : null}

          {!editorLoading && editorError ? (
            <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {editorError}
            </div>
          ) : null}

          {!editorLoading && !editorError && editorDetail ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <TextField
                  label="Template Name"
                  value={editorForm.templateName}
                  onChange={(event) => handleFormChange('templateName', event.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Suitable For"
                  value={editorForm.suitableFor}
                  onChange={(event) => handleFormChange('suitableFor', event.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Clinical Tags"
                  value={editorForm.clinicalTagsCsv}
                  onChange={(event) => handleFormChange('clinicalTagsCsv', event.target.value)}
                  fullWidth
                  size="small"
                  placeholder="PCOS, Low-GI"
                />
                <TextField
                  label="Visibility"
                  value={editorForm.visibility}
                  onChange={(event) => handleFormChange('visibility', event.target.value as 'private' | 'shared_with_practice')}
                  fullWidth
                  size="small"
                  select
                  SelectProps={{ native: true }}
                >
                  <option value="private">Private</option>
                  <option value="shared_with_practice">Public</option>
                </TextField>
              </div>

              <TextField
                label="Description"
                value={editorForm.description}
                onChange={(event) => handleFormChange('description', event.target.value)}
                fullWidth
                multiline
                minRows={3}
                size="small"
              />

              <div className="rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] p-3">
                <p className="mb-2 text-sm font-semibold text-[#1D2B2D]">Saved Nutrition Totals</p>
                <div className="grid grid-cols-2 gap-2 text-sm text-[#4A5565] md:grid-cols-5">
                  <span>Calories: <strong>{formatNutritionApiRaw(editorDetail.totals?.calories)}</strong></span>
                  <span>Protein: <strong>{formatNutritionApiRaw(editorDetail.totals?.protein)}g</strong></span>
                  <span>Carbs: <strong>{formatNutritionApiRaw(editorDetail.totals?.carbs)}g</strong></span>
                  <span>Fat: <strong>{formatNutritionApiRaw(editorDetail.totals?.fat)}g</strong></span>
                  <span>Fiber: <strong>{formatNutritionApiRaw(editorDetail.totals?.fiber)}g</strong></span>
                </div>
              </div>

              <div className="space-y-3">
                {editorDays.map((day, dayIndex) => (
                  <div key={`${day.dayId}-${dayIndex}`} className="rounded-lg border border-[#E5E7EB] p-3">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-[#1D2B2D]">Day {day.dayNumber}</span>
                      <span className="text-xs text-[#6B7280]">{day.date || ''}</span>
                    </div>
                    <TextField
                      label="Day Name"
                      value={day.dayName || ''}
                      onChange={(event) => handleDayNameChange(dayIndex, event.target.value)}
                      size="small"
                      fullWidth
                      sx={{ mb: 2 }}
                    />

                    <div className="space-y-3">
                      {day.meals.map((meal, mealIndex) => (
                        <div key={`${meal.mealId}-${mealIndex}`} className="rounded-md border border-[#E5E7EB] bg-white p-3">
                          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                            <TextField
                              label="Meal Name"
                              value={meal.mealName}
                              onChange={(event) => handleMealFieldChange(dayIndex, mealIndex, 'mealName', event.target.value)}
                              size="small"
                              fullWidth
                            />
                            <TextField
                              label="Meal Type"
                              value={meal.mealType}
                              onChange={(event) => handleMealFieldChange(dayIndex, mealIndex, 'mealType', event.target.value)}
                              size="small"
                              fullWidth
                            />
                            <TextField
                              label="Time"
                              value={meal.time || ''}
                              onChange={(event) => handleMealFieldChange(dayIndex, mealIndex, 'time', event.target.value)}
                              size="small"
                              fullWidth
                            />
                          </div>

                          <div className="mt-2 space-y-2">
                            {meal.items.map((item, itemIndex) => (
                              <div key={`${item.id}-${itemIndex}`} className="rounded border border-[#F1F5F9] bg-[#F8FAFC] px-2 py-2">
                                <div className="mb-1 text-xs font-semibold text-[#1F2937]">{item.name}</div>
                                <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                                  <TextField
                                    label="Servings"
                                    type="number"
                                    value={item.servings}
                                    onChange={(event) => handleItemServingsChange(dayIndex, mealIndex, itemIndex, event.target.value)}
                                    size="small"
                                    fullWidth
                                  />
                                  <TextField
                                    label="Calories"
                                    value={formatNutritionApiRaw(item.nutrition?.calories)}
                                    size="small"
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                  />
                                  <TextField
                                    label="Protein (g)"
                                    value={formatNutritionApiRaw(item.nutrition?.protein)}
                                    size="small"
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                  />
                                  <TextField
                                    label="Carbs (g)"
                                    value={formatNutritionApiRaw(item.nutrition?.carbohydrate ?? item.nutrition?.carbs)}
                                    size="small"
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeEditor} disabled={editorSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={editorSaving || editorLoading || !editorTemplateId}
            sx={{ backgroundColor: ACCENT, '&:hover': { backgroundColor: '#159BC6' } }}
          >
            {editorSaving ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatesPage;
